import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi } from '@/api/userApi';
import { PageHeader } from '@/components/shared/PageHeader';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
export const UserListPage = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [totalUsers, setTotalUsers] = useState(0);
    const [filters, setFilters] = useState({
        page: 1,
        perPage: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc',
    });
    useEffect(() => {
        loadUsers();
    }, [filters]);
    const loadUsers = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await userApi.getUsers(filters);
            setUsers(response.users);
            setTotalUsers(response.total);
        }
        catch (err) {
            setError('Failed to load users. Please try again.');
            console.error('Load users error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleSort = (column) => {
        setFilters((prev) => ({
            ...prev,
            sortBy: column,
            sortOrder: prev.sortBy === column && prev.sortOrder === 'asc' ? 'desc' : 'asc',
        }));
    };
    const handleStatusFilter = (status) => {
        setFilters((prev) => ({
            ...prev,
            status: status || undefined,
            page: 1,
        }));
    };
    const handleRoleFilter = (role) => {
        setFilters((prev) => ({
            ...prev,
            role: role || undefined,
            page: 1,
        }));
    };
    const handleSearch = (search) => {
        setFilters((prev) => ({
            ...prev,
            search: search || undefined,
            page: 1,
        }));
    };
    const columns = [
        {
            key: 'username',
            label: 'Username',
            sortable: true,
            render: (user) => (_jsxs("div", { className: "flex items-center gap-3", children: [user.avatar ? (_jsx("img", { src: user.avatar, alt: user.username || user.email, className: "w-8 h-8 rounded-full" })) : (_jsx("div", { className: "w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold", children: (user.username || user.email)[0].toUpperCase() })), _jsx("span", { className: "font-medium text-gray-900", children: user.username || user.email })] })),
        },
        {
            key: 'email',
            label: 'Email',
            sortable: true,
            render: (user) => _jsx("span", { className: "text-gray-600", children: user.email }),
        },
        {
            key: 'accountStatus',
            label: 'Status',
            render: (user) => (_jsx(StatusBadge, { status: user.accountStatus === 'active' ? 'active' : 'inactive', label: user.accountStatus })),
        },
        {
            key: 'subscriptionTier',
            label: 'Tier',
            render: (user) => (_jsx("span", { className: "text-sm text-gray-600 capitalize", children: user.subscriptionTier })),
        },
        {
            key: 'createdAt',
            label: 'Created',
            sortable: true,
            render: (user) => (_jsx("span", { className: "text-sm text-gray-600", children: new Date(user.createdAt).toLocaleDateString() })),
        },
        {
            key: 'lastActiveAt',
            label: 'Last Active',
            render: (user) => (_jsx("span", { className: "text-sm text-gray-600", children: user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleDateString() : 'Never' })),
        },
    ];
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "User Management", subtitle: `${totalUsers} total users` }), error && (_jsx("div", { className: "mb-6", children: _jsx(ErrorAlert, { message: error, onDismiss: () => setError('') }) })), _jsx("div", { className: "card p-4 mb-6", children: _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4", children: [_jsx("input", { type: "text", placeholder: "Search by name or email...", onChange: (e) => handleSearch(e.target.value), className: "input" }), _jsxs("select", { onChange: (e) => handleStatusFilter(e.target.value), className: "input", children: [_jsx("option", { value: "", children: "All Statuses" }), _jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "suspended", children: "Suspended" }), _jsx("option", { value: "banned", children: "Banned" }), _jsx("option", { value: "inactive", children: "Inactive" })] }), _jsxs("select", { onChange: (e) => handleRoleFilter(e.target.value), className: "input", children: [_jsx("option", { value: "", children: "All Roles" }), _jsx("option", { value: "user", children: "User" }), _jsx("option", { value: "admin", children: "Admin" })] }), _jsx("button", { onClick: loadUsers, className: "btn-secondary", children: "Refresh" })] }) }), _jsx(DataTable, { columns: columns, data: users, isLoading: isLoading, emptyMessage: "No users found", onSort: handleSort, sortColumn: filters.sortBy, sortDirection: filters.sortOrder, onRowClick: (user) => navigate(`/users/${user.id}`), actions: (user) => (_jsx("button", { onClick: (e) => {
                        e.stopPropagation();
                        navigate(`/users/${user.id}`);
                    }, className: "text-primary-600 hover:text-primary-900 font-medium", children: "View" })) })] }));
};
